/**
 * The frozen operation-cost arithmetic, mirrored for the *mock* API.
 *
 * `adforge/schemas/ops.py` owns this arithmetic (W0). The console never prices a push
 * itself: the Push screen renders `OpsEstimate` returned by
 * `POST /api/v1/blueprints/{id}/ops-estimate` and the `OpsBudgetStatus` returned by
 * `GET /api/v1/ops-budget`, because the number the user acknowledges must be the number
 * the *accountant* leases (04-contracts.md §4).
 *
 * This module exists for one reason: the MSW mock has to answer those two endpoints
 * with the same numbers the real API will, otherwise the demo screens lie. It is
 * checked against the golden fixture's `expected_ops_estimate.json` in `ops.test.ts`.
 */
import type { Blueprint, OpsEstimate, Platform } from '@/api/types'

/** Stable keys for OpsEstimate.by_entity; the backend contract fixes the spelling. */
export const ENTITY_KEYS = [
  'campaign_budget',
  'campaign',
  'criteria',
  'ad_group',
  'keyword',
  'negative_keyword',
  'responsive_search_ad',
  'asset',
] as const

export type EntityKey = (typeof ENTITY_KEYS)[number]

export const MAX_MUTATE_OPERATIONS_PER_REQUEST = 10_000

export interface EntityOpCosts {
  campaign_budget: number
  campaign: number
  criteria: number
  ad_group: number
  keyword: number
  negative_keyword: number
  responsive_search_ad: number
  /** Per campaign. Google pays for sitelinks/callouts; v1 ships none on Microsoft. */
  asset: number
}

/** Exactly the table in 02-platform-constraints.md. */
export const GOOGLE_OP_COSTS: EntityOpCosts = {
  campaign_budget: 1,
  campaign: 1,
  criteria: 1,
  ad_group: 1,
  keyword: 1,
  negative_keyword: 1,
  responsive_search_ad: 1,
  asset: 20,
}

export const MICROSOFT_OP_COSTS: EntityOpCosts = {
  campaign_budget: 0, // Microsoft's budget is inline on the campaign
  campaign: 1,
  criteria: 1,
  ad_group: 1,
  keyword: 1,
  negative_keyword: 1,
  responsive_search_ad: 1,
  asset: 0, // v1 ships no Microsoft assets
}

export const PLATFORM_OP_COSTS: Record<Platform, EntityOpCosts> = {
  google: GOOGLE_OP_COSTS,
  microsoft: MICROSOFT_OP_COSTS,
}

/** Daily ceilings by granted access level (02-platform-constraints.md). */
export const GOOGLE_OPS_PER_DAY_BY_ACCESS_LEVEL: Record<string, number> = {
  test: 15_000,
  explorer: 2_880,
  basic: 15_000,
  standard: 0, // 0 == unlimited, subject to per-service limits
}

/** One realistic push, per 02-platform-constraints.md: ~390 operations. */
export const REFERENCE_PUSH_OPERATIONS = 390

export function estimateOperations(blueprint: Blueprint, platform: Platform): OpsEstimate {
  const costs = PLATFORM_OP_COSTS[platform]
  const byEntity: Record<string, number> = Object.fromEntries(
    ENTITY_KEYS.map((key) => [key, 0]),
  ) as Record<string, number>

  for (const campaign of blueprint.campaigns) {
    byEntity.campaign_budget! += costs.campaign_budget
    byEntity.campaign! += costs.campaign
    byEntity.criteria! +=
      (campaign.geo_targets.length + campaign.language_targets.length) * costs.criteria
    byEntity.asset! += costs.asset
    for (const adGroup of campaign.ad_groups) {
      byEntity.ad_group! += costs.ad_group
      byEntity.keyword! += adGroup.keywords.length * costs.keyword
      byEntity.negative_keyword! += (adGroup.negatives ?? []).length * costs.negative_keyword
      byEntity.responsive_search_ad! += adGroup.ads.length * costs.responsive_search_ad
    }
  }
  byEntity.negative_keyword! +=
    (blueprint.shared_negatives ?? []).reduce((sum, list) => sum + list.keywords.length, 0) *
    costs.negative_keyword

  const total = Object.values(byEntity).reduce((a, b) => a + b, 0)
  return {
    total_operations: total,
    by_entity: byEntity,
    requests_required: Math.max(1, Math.ceil(total / MAX_MUTATE_OPERATIONS_PER_REQUEST)),
    platform,
  }
}

/**
 * How many pushes of this size still fit in today's remaining quota. Shown *next to*
 * the raw numbers so the user can feel the scarcity (05-risks-and-verification.md R1:
 * on Explorer access the whole platform affords ~7 pushes/day).
 */
export function pushesRemaining(estimate: OpsEstimate, availableOperations: number): number {
  if (estimate.total_operations <= 0) return 0
  return Math.floor(availableOperations / estimate.total_operations)
}
